import parseHtml, {
  type DOMNode,
  domToReact,
  Element,
} from 'html-react-parser';

import { Button } from '@/components/ui/Button';
import { YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { handleExternalUrlClick } from '@/utils/platform';

import { type ChangelogEntry } from './changelog.feed';
import { useChangelogEntryPageQuery } from './changelog.query';
import { ChangelogPrompt } from './ChangelogPrompt';

const isPromptBody = (node: DOMNode) =>
  node instanceof Element && 'data-prompt-body' in node.attribs;

const replacePrompt = (node: DOMNode) => {
  if (!(node instanceof Element) || !('data-prompt' in node.attribs)) return;
  const promptBody = (node.children as DOMNode[]).find(isPromptBody);
  if (!(promptBody instanceof Element)) return;
  return (
    <ChangelogPrompt>
      {domToReact(promptBody.children as DOMNode[])}
    </ChangelogPrompt>
  );
};

export const ChangelogEntryContent = ({ entry }: { entry: ChangelogEntry }) => {
  const { data: page, isPending, isPaused } = useChangelogEntryPageQuery(entry);

  if (isPending && !isPaused) {
    return (
      <YStack className="mt-6 gap-3">
        <Skeleton className="aspect-video w-full rounded-xl" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </YStack>
    );
  }

  if (!page?.contentHtml) {
    return (
      <YStack className="mt-6 items-start gap-3 text-sm text-foreground-muted">
        <span>This update couldn't load.</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExternalUrlClick(entry.url)}
        >
          Open in browser
        </Button>
      </YStack>
    );
  }

  return (
    <div className="changelog-entry prose prose-bird mt-2 max-w-none select-text prose-h2:text-lg">
      {parseHtml(page.contentHtml, { replace: replacePrompt })}
    </div>
  );
};
